'use client';

import { FC, ReactNode, useState, useMemo, type JSX, useEffect, useRef } from 'react';

import { asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk';

import { useEffectOnce, useMount, useUpdateEffect } from 'shared/base';

import { FeatureFlagsContext } from './context';
import { BaseAuthUser } from '../../../baseAuth/context';

import { useBaseAuth } from '../../../baseAuth/hook';

import { FeatureFlag, localFeatureFlagValues } from '../../constants';

interface Props {
  children: ReactNode;
  clientIdOverride?: string;
  customAttributes?: {
    isCQInternal?: boolean;
    isPlatform?: boolean;
  };
  isPlatform?: boolean;
  teamNameOverride?: string;
}

const clientId = process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_ID;

const LOCAL_STORAGE_KEYS = {
  OVERRIDE_ACCESS: 'feature-flags:override-access',
  OVERRIDE_VALUES: 'feature-flags:override-values',
};

function getStableDeviceId() {
  const KEY = 'ld_device_id';
  let id = typeof window === 'undefined' ? undefined : localStorage.getItem(KEY);
  if (!id) {
    id = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) + Date.now();
    if (typeof window !== 'undefined') {
      localStorage.setItem(KEY, id);
    }
  }

  return id;
}
function IdentifyOnAuthChange({
  customAttributes,
  deviceKey,
  onInitialized,
  teamName,
  user,
}: {
  customAttributes?: Record<string, unknown>;
  deviceKey: string;
  onInitialized: () => void;
  teamName?: string;
  user?: BaseAuthUser | null;
}) {
  const ldClient = useLDClient();
  const prevCtxJson = useRef<string | null>(null);

  const baseAttrs = useMemo(
    () => ({
      hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
      ...customAttributes,
    }),
    [customAttributes],
  );

  const context = useMemo(() => {
    const email = user?.email || undefined;
    const emailDomain = email?.split?.('@')[1];

    const userAttrs = {
      ...baseAttrs,
      email_domain: emailDomain,
      seed: deviceKey,
      team: teamName,
    };

    return email
      ? {
          device: {
            anonymous: true,
            key: deviceKey,
            kind: 'device' as const,
            seed: deviceKey,
            ...baseAttrs,
            custom: baseAttrs, // legacy custom path (temporary for migration)
          },
          kind: 'multi' as const,
          user: {
            anonymous: false,
            email,
            key: user!.id,
            kind: 'user' as const,
            ...userAttrs,
            custom: userAttrs, // legacy custom path (temporary for migration)
          },
        }
      : {
          anonymous: true,
          key: deviceKey,
          kind: 'device' as const,
          seed: deviceKey,
          ...baseAttrs,
          custom: baseAttrs, // legacy custom path (temporary for migration)
        };
  }, [user, baseAttrs, teamName, deviceKey]);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    const json = JSON.stringify(context);
    if (json !== prevCtxJson.current) {
      ldClient.identify(context);
      prevCtxJson.current = json;
    }
  }, [ldClient, context]);

  useMount(() => {
    (async () => {
      await ldClient?.waitUntilReady();
      onInitialized();
    })();
  });

  return null;
}
export const FeatureFlagsProvider: FC<Props> = ({
  children,
  clientIdOverride,
  customAttributes,
  teamNameOverride,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { currentMembership, user } = useBaseAuth();
  const teamName = teamNameOverride ?? currentMembership?.team?.name;
  const LDClientId = clientIdOverride || clientId;

  const deviceKeyRef = useRef<string>(getStableDeviceId());

  const [LDProvider, setLDProvider] =
    useState<({ children }: { children: ReactNode }) => JSX.Element>();

  const [sessionFeatureFlags, setSessionFeatureFlags] = useState<
    Partial<typeof localFeatureFlagValues>
  >(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEYS.OVERRIDE_VALUES);

      return stored ? (JSON.parse(stored) as Partial<typeof localFeatureFlagValues>) : {};
    } catch {
      return {};
    }
  });

  const allowedEditingFeatureFlags = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.localStorage.getItem(LOCAL_STORAGE_KEYS.OVERRIDE_ACCESS) === 'true',
    [],
  );

  useEffectOnce(() => {
    (async () => {
      const baseAttrs = {
        hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
        ...customAttributes,
      };

      const email = user?.email || undefined;
      const emailDomain = email?.split?.('@')[1];
      const userAttrs = {
        ...baseAttrs,
        email_domain: emailDomain,
        seed: deviceKeyRef.current,
        teamName,
      };

      const initialContext = email
        ? {
            device: {
              anonymous: true,
              key: deviceKeyRef.current,
              kind: 'device' as const,
              seed: deviceKeyRef.current,
              ...baseAttrs,
              custom: baseAttrs, // legacy custom (temporary)
            },
            kind: 'multi' as const,
            user: {
              anonymous: false,
              email,
              key: user!.id,
              kind: 'user' as const,
              team: teamName,
              ...userAttrs,
              custom: userAttrs, // legacy custom (temporary)
            },
          }
        : {
            anonymous: true,
            key: deviceKeyRef.current,
            kind: 'device' as const,
            seed: deviceKeyRef.current,
            ...baseAttrs,
            custom: baseAttrs, // legacy custom (temporary)
          };

      const wrapper = await asyncWithLDProvider({
        clientSideID: LDClientId!,
        context: initialContext,
        options: { bootstrap: 'localStorage' },
        reactOptions: { useCamelCaseFlagKeys: false },
      });

      setLDProvider(() => wrapper);
    })();
  }, !!LDClientId);

  useMount(() => {
    if (!LDClientId) {
      setIsInitialized(true);
    }
  });

  useUpdateEffect(() => {
    try {
      window.localStorage.setItem(
        LOCAL_STORAGE_KEYS.OVERRIDE_VALUES,
        JSON.stringify(sessionFeatureFlags),
      );
    } catch {
      /* ignore storage errors */
    }
  }, [sessionFeatureFlags]);

  return (
    <FeatureFlagsContext.Provider
      value={{
        allowedEditingFeatureFlags,
        featureFlags: localFeatureFlagValues,
        featureFlagsToOverride: sessionFeatureFlags,
        isInitialized,
        resetFeatureFlagLocally: (key: FeatureFlag) => {
          setSessionFeatureFlags((prev) => {
            const { [key]: _omit, ...rest } = prev;

            return rest;
          });
        },
        setFeatureFlagLocally: (key, value) => {
          setSessionFeatureFlags((prev) => ({ ...prev, [key]: value }));
        },
      }}
    >
      {LDProvider ? (
        <LDProvider>
          <IdentifyOnAuthChange
            customAttributes={customAttributes}
            deviceKey={deviceKeyRef.current}
            onInitialized={() => setIsInitialized(true)}
            teamName={teamName}
            user={user}
          />
          {children}
        </LDProvider>
      ) : (
        children
      )}
    </FeatureFlagsContext.Provider>
  );
};
